import { AsyncPipe, CurrencyPipe, DecimalPipe, NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { DepotFacade } from '@zero/depot/domain';
import { MicarContractsNudgingComponent } from '@zero/shared/feature-contract-confirmation/web';
import { expandHeight, expandTdDivsAnimation, fadeInAnimation, onlyEnter } from '@zero/shared/ui-animations';
import { BlinkDirective } from '@zero/shared/ui-directives';
import { NotesSafeHtmlPipe } from '@zero/shared/ui-pipes';
import { PushQuoteService } from '@zero/shared/ui-push';
import { SortHelper, SorterLabelComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent, ListToggleComponent } from '@zero/shared/ui-web-design-system';
import { CustomerStateFacade, PositionsFacade } from '@zero/shared/util-global-state';
import { CryptoSettings, Customer, Position, PositionFilterType, Sum } from '@zero/shared/util-models';
import { AuthService, DataService } from '@zero/shared/util-web-app-services';
import { commonRoutes } from 'constants/web-app-constants';
import { environment } from 'environments/environment';
import { LOCAL_STORAGE, StorageService } from 'ngx-webstorage-service';
import { BehaviorSubject } from 'rxjs';
import { CryptoComponent } from '../../components/crypto/crypto.component';

const CRYPTO_SETTINGS_CACHE_KEY = 'crypto-settings';

@Component({
  selector: 'home-cryptos',
  templateUrl: './cryptos.component.html',
  animations: [fadeInAnimation, expandTdDivsAnimation, expandHeight, onlyEnter],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ListToggleComponent,
    NgTemplateOutlet,
    SorterLabelComponent,
    CryptoComponent,
    FormsModule,
    RouterLink,
    BlinkDirective,
    AsyncPipe,
    DecimalPipe,
    CurrencyPipe,
    TranslocoPipe,
    NotesSafeHtmlPipe,
    ButtonComponent,
    UpperCasePipe,
    MicarContractsNudgingComponent,
  ],
})
export class CryptosComponent implements OnInit, OnDestroy, OnChanges {
  public readonly positionsFacade = inject(PositionsFacade);
  public readonly depotFacade = inject(DepotFacade);

  @Input() public actionsEnabled = true;
  @Input() public positions: Position[] = [];

  public readonly commonRoutes = commonRoutes;
  public readonly environment = environment;
  public isPotentialCustomer = false;
  public showPositions = true;
  public sorter: SortHelper;
  public currentCustomer: Customer;
  public sums: Sum[] = [];

  @ViewChild('noteTextArea') public note: ElementRef<HTMLTextAreaElement>;

  public currentlyEditingNote = {
    isin: undefined,
    note: undefined,
  };
  public currentlyEditingEntryQuote?: string;

  readonly #cryptoSettings = new BehaviorSubject<CryptoSettings | undefined>(undefined);
  public readonly cryptoSettings$ = this.#cryptoSettings.asObservable();

  public readonly dataService = inject(DataService);
  public readonly customerStateFacade = inject(CustomerStateFacade);
  readonly #quotesService = inject(PushQuoteService);
  readonly #authService = inject(AuthService);
  readonly #cd = inject(ChangeDetectorRef);
  readonly #destroyRef = inject(DestroyRef);

  constructor(@Inject(LOCAL_STORAGE) private _storage: StorageService) {
    this.sorter = new SortHelper('assetName', true, 'cryptos');
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { positions } = changes;
    positions && this.#setPositions();
  }

  public ngOnInit(): void {
    this.#loadCryptoSettings();
    this.#authService
      .isPotentialCustomer()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((isPotentialCustomer) => {
        this.isPotentialCustomer = isPotentialCustomer;

        if (!this.isPotentialCustomer) {
          this.#setPositions();
          this.#loadCustomer();
        }
      });
  }

  public ngOnDestroy(): void {
    this.#quotesService.unsubscribeMulti(this.positions.map(({ isin }) => isin));
  }

  #loadCryptoSettings(): void {
    const settings: CryptoSettings = this._storage.get(CRYPTO_SETTINGS_CACHE_KEY) ?? {
      notesVisible: false,
    };
    if (settings) {
      this.#cryptoSettings.next(settings);
    }
  }

  #updatePositionsSettings(settings: Partial<CryptoSettings>): void {
    const currentSettings = this.#cryptoSettings.getValue();
    this.#cryptoSettings.next({ ...currentSettings, ...settings });
    this._storage.set(CRYPTO_SETTINGS_CACHE_KEY, this.#cryptoSettings.getValue());
  }

  #loadCustomer(): void {
    this.currentCustomer = this.dataService.getSelectedCustomerItem();
  }

  public sortBy(sortAttr: string): void {
    this.sorter.sortBy(this.positions, sortAttr);
  }

  public getSums = (type: string): Sum[] => this.sums.filter((sum) => sum.type === type);

  #setPositions(): void {
    this.#calculateSums();
    this.sortAndFilter();
  }

  #getSum = (positions: Position[]): Sum => {
    const entryValue = positions.filter((pos) => this.#includeInSum(pos)).reduce((acc, curr) => acc + curr.entryValue, 0);
    const currentValue = positions.filter((pos) => this.#includeInSum(pos)).reduce((acc, curr) => acc + curr.currentValue, 0);
    const perfAbs = currentValue - entryValue;
    const perfPct = entryValue ? (perfAbs / entryValue) * 100 : 0;
    const perfTodayAbs = positions.filter((pos) => this.#includeInSum(pos)).reduce((acc, curr) => acc + curr.perfTodayAbs, 0);
    const perfTodayPct = currentValue ? (currentValue / (currentValue - perfTodayAbs) - 1) * 100 : 0;

    return {
      currentValue,
      entryValue,
      perfAbs,
      perfPct,
      perfTodayAbs,
      perfTodayPct,
    };
  };

  #calculateSums(): void {
    this.sums = [];

    const type = PositionFilterType.ALL;
    !this.sums.some((sum) => sum.type === type) &&
      this.sums.push({
        type,
        ...this.#getSum(this.positions),
      });
  }

  #includeInSum(p: Position): boolean {
    return !p.endfaellig && !p.knockedOutFlag;
  }

  public sortAndFilter = async (): Promise<void> => {
    this.sorter.sortValues(this.positions);
    this.positions = [...this.positions];
    this.#calculateSums();
  };

  public toggleNotes(settings: CryptoSettings): void {
    this.#updatePositionsSettings({ notesVisible: !settings.notesVisible });
  }

  public editNote(isin: string, note: string): void {
    this.currentlyEditingEntryQuote = undefined;
    this.currentlyEditingNote = { isin, note };
    this.#cd.detectChanges();
    this.note.nativeElement.focus();
  }

  public editEntryQuote(isin: string): void {
    this.currentlyEditingNote = undefined;
    this.currentlyEditingEntryQuote = isin;
  }

  public updateEntryQuote = (entryQuote: number): void => {
    this.positionsFacade.updatePositionEntryQuote(this.currentlyEditingEntryQuote, entryQuote);
    this.currentlyEditingEntryQuote = undefined;
  };

  public onSubmitNote(note: string, isin: string, settings: CryptoSettings): void {
    this.positionsFacade.updatePositionsNote(isin, note);
    this.currentlyEditingNote = { isin: undefined, note: undefined };
    this.#updatePositionsSettings({ notesVisible: settings.notesVisible });
  }
}
